import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { subirImagenAFirebase } from 'scripts/firebaseUpload';
import { obtenerDescuentos } from 'scripts/obtenerDescuentos';
import { useDispatch, useSelector } from 'react-redux';
import { obtenerViandasAdmin } from 'scripts/obtenerViandasAdmin';
import { crearVianda } from 'scripts/crearVianda';
import { actualizarVianda } from 'scripts/actualizarVianda';
import { borrarVianda } from 'scripts/borrarVianda';
import { eliminarVianda } from 'scripts/eliminarVianda';
import { obtenerMenuActualViandas } from 'scripts/obtenerMenuActualViandas';
import { cargarMenuSemanalViandas } from 'scripts/cargarMenuSemanalViandas';
import { listarComprasViandas } from 'scripts/listarComprasViandas';
import { marcarEntregaVianda } from 'scripts/marcarEntregaVianda';
import { obtenerTelefonoWhatsAppUruguay } from 'utils/userUtils';
import CustomDialog from 'helpers/Dialog';

const initialViandaForm = {
	id: null,
	titulo: '',
	descripcion: '',
	imagen: null,
	cantidad_dias: 5,
	precio_por_vianda: '',
	descuento_id: '',
	activo: true,
};

const normalizarLista = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.viandas)) return payload.viandas;
	return [];
};

const getCompraMeta = (compra) => {
	const total =
		compra?.total_entregas ||
		compra?.viandas_totales ||
		compra?.cantidad_dias ||
		compra?.plan?.cantidad_dias ||
		compra?.vianda?.cantidad_dias ||
		0;
	const entregas = Array.isArray(compra?.entregas) ? compra.entregas : [];
	const entregadas =
		compra?.entregas_entregadas || compra?.entregasRealizadas || entregas.filter((entrega) => entrega?.entregada).length;
	return {
		total,
		entregadas,
		tieneDetalleEntregas: entregas.length > 0,
	};
};

const normalizarFecha = (fecha) => {
	if (!fecha) return '';
	if (typeof fecha === 'string') return fecha.slice(0, 10);
	return String(fecha).slice(0, 10);
};

const renderTelefonoCliente = (telefono) => {
	if (!telefono || telefono === '-') {
		return '-';
	}

	const telefonoWhatsapp = obtenerTelefonoWhatsAppUruguay(telefono);
	if (!telefonoWhatsapp) {
		return (
			<Typography variant="body2" sx={{ color: 'text.secondary' }}>
				{telefono}
			</Typography>
		);
	}

	return (
		<Link
			href={`https://wa.me/${telefonoWhatsapp}`}
			target="_blank"
			rel="noopener noreferrer"
			underline="hover"
			sx={{ fontSize: '0.875rem' }}
		>
			{telefono}
		</Link>
	);
};

const esPedidoAdmin = (compra) =>
	Boolean(compra?.es_pedido_admin) || String(compra?.cliente_nombre || '').trim().toLowerCase() === 'cliente generico';

const obtenerTextoCliente = (compra) => {
	if (esPedidoAdmin(compra)) {
		const notasPedido = String(compra?.notas || '').trim();
		return notasPedido || 'Sin notas';
	}

	return compra?.cliente?.nombre || compra?.usuario?.nombre || compra?.cliente_nombre || '-';
};

const esDiaHabil = (fecha) => {
	const date = new Date(`${normalizarFecha(fecha)}T00:00:00`);
	const day = date.getDay();
	return day !== 0 && day !== 6;
};

const getEntregasValidas = (compra) => {
	const fechaInicio = normalizarFecha(compra?.fecha_inicio || compra?.inicio);
	const entregas = Array.isArray(compra?.entregas) ? compra.entregas : [];

	return entregas
		.filter((entrega) => {
			const fechaEntrega = normalizarFecha(entrega?.fecha_entrega);
			return fechaEntrega && (!fechaInicio || fechaEntrega >= fechaInicio) && esDiaHabil(fechaEntrega);
		})
		.sort((a, b) => normalizarFecha(a.fecha_entrega).localeCompare(normalizarFecha(b.fecha_entrega)));
};

const formatearFecha = (fecha) => {
	const value = normalizarFecha(fecha);
	if (!value) return '-';
	const [year, month, day] = value.split('-');
	return `${day}/${month}/${year}`;
};

const formatearFechaHora = (fechaHora) => {
	if (!fechaHora) return '-';
	const date = new Date(fechaHora);
	if (Number.isNaN(date.getTime())) return '-';
	return date.toLocaleString('es-UY', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const agruparEntregasPorSemana = (entregas) =>
	entregas.reduce((semanas, entrega, index) => {
		const semana = Math.floor(index / 5) + 1;
		if (!semanas[semana]) {
			semanas[semana] = [];
		}
		semanas[semana].push(entrega);
		return semanas;
	}, {});

const ViandasManagement = () => {
	const dispatch = useDispatch();
	const descuentos = useSelector((state) => state.descuentos || []);
	const token = sessionStorage.getItem('token');

	const [viandas, setViandas] = useState([]);
	const [compras, setCompras] = useState([]);
	const [menuActual, setMenuActual] = useState(null);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [viandaForm, setViandaForm] = useState(initialViandaForm);
	const [menuForm, setMenuForm] = useState({ imagen: null, semana_inicio: '' });
	const [entregaModalCompra, setEntregaModalCompra] = useState(null);
	const [entregasSeleccionadas, setEntregasSeleccionadas] = useState({});
	const [notaModal, setNotaModal] = useState({ open: false, fechaEntrega: '', nota: '' });
	const [viandaAEliminar, setViandaAEliminar] = useState(null);
	const [incluirCompletadas, setIncluirCompletadas] = useState(false);

	const descuentosFiltrados = useMemo(
		() => descuentos.filter((descuento) => descuento.descripcion !== 'Cupon Nutricionista'),
		[descuentos],
	);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const [viandasRes, comprasRes, menuRes] = await Promise.all([
				obtenerViandasAdmin(token, true),
				listarComprasViandas(token, incluirCompletadas),
				obtenerMenuActualViandas(),
			]);
			setViandas(normalizarLista(viandasRes));
			setCompras(normalizarLista(comprasRes));
			setMenuActual(menuRes || null);
		} catch (error) {
			toast.error(error.message || 'Error al cargar información de viandas.');
		} finally {
			setLoading(false);
		}
	}, [token, incluirCompletadas]);

	useEffect(() => {
		obtenerDescuentos(dispatch, token).catch((error) => {
			toast.error(error.message || 'No se pudieron cargar los descuentos.');
		});
		fetchData();
	}, [dispatch, fetchData, token]);

	const syncEntregaActualizada = useCallback((compraId, entregaActualizada) => {
		const fechaEntregaActualizada = normalizarFecha(entregaActualizada?.fecha_entrega);
		if (!fechaEntregaActualizada) {
			return;
		}

		const actualizarCompra = (compra) => {
			if (!compra || compra.id !== compraId) {
				return compra;
			}

			return {
				...compra,
				entregas: (Array.isArray(compra.entregas) ? compra.entregas : []).map((entrega) =>
					normalizarFecha(entrega?.fecha_entrega) === fechaEntregaActualizada ? { ...entrega, ...entregaActualizada } : entrega,
				),
			};
		};

		setEntregaModalCompra((prev) => actualizarCompra(prev));
		setCompras((prev) => prev.map((compra) => actualizarCompra(compra)));
	}, []);

	const handleOpen = (vianda = null) => {
		if (vianda) {
			setViandaForm({
				id: vianda.id,
				titulo: vianda.titulo || '',
				descripcion: vianda.descripcion || '',
				imagen: vianda.imagen || null,
				cantidad_dias: Number(vianda.cantidad_dias) || 5,
				precio_por_vianda: vianda.precio_por_vianda ?? '',
				descuento_id: vianda.descuento_id ?? '',
				activo: vianda.activo !== false,
			});
		} else {
			setViandaForm(initialViandaForm);
		}
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setViandaForm(initialViandaForm);
	};

	const handleGuardarVianda = async () => {
		if (!viandaForm.titulo || !viandaForm.descripcion || !viandaForm.precio_por_vianda || !viandaForm.cantidad_dias) {
			toast.error('Completá título, descripción, días y precio por vianda.');
			return;
		}
		if (![5, 20].includes(Number(viandaForm.cantidad_dias))) {
			toast.error('Los planes de viandas deben ser de 5 o 20 días.');
			return;
		}

		try {
			setLoading(true);
			let imagenUrl = viandaForm.imagen;
			if (viandaForm.imagen && typeof viandaForm.imagen !== 'string') {
				imagenUrl = await subirImagenAFirebase(viandaForm.imagen);
			}

			const payload = {
				titulo: viandaForm.titulo,
				descripcion: viandaForm.descripcion,
				imagen: imagenUrl || '',
				cantidad_dias: Number(viandaForm.cantidad_dias),
				precio_por_vianda: Number(viandaForm.precio_por_vianda),
				descuento_id: viandaForm.descuento_id ? Number(viandaForm.descuento_id) : null,
				activo: Boolean(viandaForm.activo),
			};

			if (viandaForm.id) {
				await actualizarVianda({ id: viandaForm.id, ...payload }, token);
				toast.success('Vianda actualizada correctamente.');
			} else {
				await crearVianda(payload, token);
				toast.success('Vianda creada correctamente.');
			}

			handleClose();
			await fetchData();
		} catch (error) {
			toast.error(error.message || 'No se pudo guardar la vianda.');
		} finally {
			setLoading(false);
		}
	};

	const handleCambiarEstadoVianda = async (vianda) => {
		const estaActiva = vianda.activo !== false;
		try {
			setLoading(true);
			if (estaActiva) {
				await borrarVianda(vianda.id, token);
				toast.success('Vianda desactivada correctamente.');
			} else {
				await actualizarVianda({ id: vianda.id, activo: true }, token);
				toast.success('Vianda activada correctamente.');
			}
			await fetchData();
		} catch (error) {
			toast.error(error.message || `No se pudo ${estaActiva ? 'desactivar' : 'activar'} la vianda.`);
		} finally {
			setLoading(false);
		}
	};

	const handleAbrirDialogoEliminar = (vianda) => {
		setViandaAEliminar(vianda);
	};

	const handleCerrarDialogoEliminar = () => {
		setViandaAEliminar(null);
	};

	const handleConfirmarEliminacion = async () => {
		if (!viandaAEliminar) {
			return;
		}

		const vianda = viandaAEliminar;
		setViandaAEliminar(null);
		try {
			setLoading(true);
			await eliminarVianda(vianda.id, token);
			toast.success('Vianda eliminada correctamente.');
			await fetchData();
		} catch (error) {
			toast.error(error.message || 'No se pudo eliminar la vianda.');
		} finally {
			setLoading(false);
		}
	};

	const handleCargarMenuSemanal = async () => {
		if (!menuForm.imagen || !menuForm.semana_inicio) {
			toast.error('Seleccioná imagen y semana de inicio.');
			return;
		}
		try {
			setLoading(true);
			const imageUrl = await subirImagenAFirebase(menuForm.imagen);
			await cargarMenuSemanalViandas(
				{
					imagen_url: imageUrl,
					semana_inicio: menuForm.semana_inicio,
				},
				token,
			);
			toast.success('Menú semanal cargado correctamente.');
			setMenuForm({ imagen: null, semana_inicio: '' });
			await fetchData();
		} catch (error) {
			toast.error(error.message || 'No se pudo cargar el menú semanal.');
		} finally {
			setLoading(false);
		}
	};

	const handleOpenEntregas = (compra) => {
		const seleccionInicial = {};
		getEntregasValidas(compra).forEach((entrega) => {
			seleccionInicial[normalizarFecha(entrega.fecha_entrega)] = Boolean(entrega.entregada);
		});
		setEntregaModalCompra(compra);
		setEntregasSeleccionadas(seleccionInicial);
	};

	const handleCloseEntregas = () => {
		setEntregaModalCompra(null);
		setEntregasSeleccionadas({});
		setNotaModal({ open: false, fechaEntrega: '', nota: '' });
	};

	const handleToggleEntrega = (fechaEntrega) => {
		setEntregasSeleccionadas((prev) => ({
			...prev,
			[fechaEntrega]: !prev[fechaEntrega],
		}));
	};

	const handleOpenNota = (entrega) => {
		setNotaModal({
			open: true,
			fechaEntrega: normalizarFecha(entrega?.fecha_entrega),
			nota: entrega?.nota_admin || '',
		});
	};

	const handleCloseNota = () => {
		setNotaModal({ open: false, fechaEntrega: '', nota: '' });
	};

	const handleGuardarNota = async () => {
		if (!entregaModalCompra || !notaModal.fechaEntrega) return;

		try {
			setLoading(true);
			const entregaActualizada = await marcarEntregaVianda(
				{
					compra_id: entregaModalCompra.id,
					fecha_entrega: notaModal.fechaEntrega,
					nota_admin: notaModal.nota.trim() || null,
				},
				token,
			);
			syncEntregaActualizada(entregaModalCompra.id, entregaActualizada);
			toast.success('Nota guardada correctamente.');
			handleCloseNota();
		} catch (error) {
			toast.error(error.message || 'No se pudo guardar la nota.');
		} finally {
			setLoading(false);
		}
	};

	const handleGuardarEntregas = async () => {
		if (!entregaModalCompra) return;
		const entregas = getEntregasValidas(entregaModalCompra);
		const cambios = entregas.filter((entrega) => {
			const fechaEntrega = normalizarFecha(entrega.fecha_entrega);
			return Boolean(entrega.entregada) !== Boolean(entregasSeleccionadas[fechaEntrega]);
		});

		if (cambios.length === 0) {
			toast.info('No hay cambios para guardar.');
			handleCloseEntregas();
			return;
		}

		try {
			setLoading(true);
			await Promise.all(
				cambios.map((entrega) => {
					const fechaEntrega = normalizarFecha(entrega.fecha_entrega);
					return marcarEntregaVianda(
						{
							compra_id: entregaModalCompra.id,
							fecha_entrega: fechaEntrega,
							entregada: Boolean(entregasSeleccionadas[fechaEntrega]),
						},
						token,
					);
				}),
			);
			toast.success('Entregas actualizadas correctamente.');
			handleCloseEntregas();
			await fetchData();
		} catch (error) {
			toast.error(error.message || 'No se pudieron actualizar las entregas.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box sx={{ mt: 2 }}>
			<Box sx={{ marginBottom: 2, paddingBottom: 2, borderBottom: '2px solid #ccc' }}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={2} md={1} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
						<Button variant="contained" color="success" onClick={() => handleOpen()}>
							<AddIcon />
						</Button>
					</Grid>
				</Grid>
			</Box>

			<Alert severity="warning" sx={{ mb: 2 }}>
				Los cupones generales no aplican a carritos con viandas. Solo se permiten cupones con alcance
				{' '}"Solo viandas de 20 días", y únicamente cuando todas las viandas del carrito son planes de 20
				días. Si una vianda ya tiene descuento propio, ningún cupón aplica.
			</Alert>

			<TableContainer component={Paper} sx={{ mb: 4 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>ID</TableCell>
							<TableCell>Título</TableCell>
							<TableCell>Días</TableCell>
							<TableCell>Precio por vianda</TableCell>
							<TableCell>Activo</TableCell>
							<TableCell>Descuento</TableCell>
							<TableCell>Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{viandas.map((vianda) => (
							<TableRow key={vianda.id}>
								<TableCell>{vianda.id}</TableCell>
								<TableCell>{vianda.titulo}</TableCell>
								<TableCell>{vianda.cantidad_dias}</TableCell>
								<TableCell>${vianda.precio_por_vianda}</TableCell>
								<TableCell>{vianda.activo ? 'Sí' : 'No'}</TableCell>
								<TableCell>{vianda.descuento?.descripcion || vianda.descuento_id || '-'}</TableCell>
								<TableCell>
									<Button size="small" onClick={() => handleOpen(vianda)} startIcon={<EditIcon />}>
										Editar
									</Button>
									<Button
										size="small"
										color={vianda.activo !== false ? 'error' : 'success'}
										onClick={() => handleCambiarEstadoVianda(vianda)}
										startIcon={vianda.activo !== false ? <DeleteIcon /> : <CheckIcon />}
									>
										{vianda.activo !== false ? 'Desactivar' : 'Activar'}
									</Button>
									{vianda.activo === false && vianda.puede_eliminar !== false && (
										<Button
											size="small"
											color="error"
											onClick={() => handleAbrirDialogoEliminar(vianda)}
											startIcon={<DeleteIcon />}
										>
											Eliminar
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Typography variant="h6" sx={{ mb: 1 }}>
				Menú semanal
			</Typography>
			<Grid container spacing={2} sx={{ mb: 4 }}>
				<Grid item xs={12} md={4}>
					<TextField
						fullWidth
						type="date"
						label="Semana inicio"
						InputLabelProps={{ shrink: true }}
						value={menuForm.semana_inicio}
						onChange={(e) => setMenuForm((prev) => ({ ...prev, semana_inicio: e.target.value }))}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Button variant="outlined" component="label" fullWidth sx={{ height: '56px' }}>
						Seleccionar imagen menú
						<input
							hidden
							type="file"
							accept="image/*"
							onChange={(e) => setMenuForm((prev) => ({ ...prev, imagen: e.target.files?.[0] || null }))}
						/>
					</Button>
				</Grid>
				<Grid item xs={12} md={4}>
					<Button variant="contained" fullWidth sx={{ height: '56px' }} onClick={handleCargarMenuSemanal}>
						Cargar menú semanal
					</Button>
				</Grid>
				{menuForm.imagen && (
					<Grid item xs={12}>
						<img
							src={URL.createObjectURL(menuForm.imagen)}
							alt="preview-menu"
							style={{ maxWidth: '320px', borderRadius: 8 }}
						/>
					</Grid>
				)}
				{menuActual?.imagen_url && (
					<Grid item xs={12}>
						<Typography variant="body2">Menú actual ({menuActual?.semana_inicio || 'sin fecha'}):</Typography>
						<img src={menuActual.imagen_url} alt="menu-actual" style={{ maxWidth: '320px', borderRadius: 8 }} />
					</Grid>
				)}
			</Grid>

			<Typography variant="h6" sx={{ mb: 1 }}>
				Compras de viandas
			</Typography>
			<FormControlLabel
				control={
					<Switch
						checked={incluirCompletadas}
						onChange={(e) => setIncluirCompletadas(e.target.checked)}
						color="primary"
					/>
				}
				label="Todas"
				sx={{ mb: 1 }}
			/>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Cliente</TableCell>
							<TableCell>Inicio</TableCell>
							<TableCell>Dirección</TableCell>
							<TableCell>Teléfono</TableCell>
							<TableCell>Notas para las cocineras</TableCell>
							<TableCell>Viandas diarias</TableCell>
							<TableCell>Entregas</TableCell>
							<TableCell>Marcar entrega</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{compras.map((compra) => {
							const meta = getCompraMeta(compra);
							const cliente = obtenerTextoCliente(compra);
							const fechaInicio = compra?.fecha_inicio || compra?.inicio || '-';
							const viandasDiarias = compra?.viandas_diarias || compra?.cantidad_diaria || 1;
							const direccion =
								(typeof compra?.direccion === 'string' ? compra.direccion : compra?.direccion?.direccion) ||
								compra?.direccion_entrega?.direccion ||
								compra?.direccion_texto ||
								compra?.cliente?.direccion ||
								'-';
							const telefono =
								compra?.telefono_contacto ||
								compra?.cliente?.telefono ||
								compra?.usuario?.telefono ||
								compra?.telefono ||
								'-';
							const notasCocinera = String(compra?.notas_cocinera || '').trim();

							return (
								<TableRow key={compra.id}>
									<TableCell>{cliente}</TableCell>
									<TableCell>{formatearFecha(fechaInicio)}</TableCell>
									<TableCell>{direccion}</TableCell>
									<TableCell>{renderTelefonoCliente(telefono)}</TableCell>
									<TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 220 }}>
										{notasCocinera || '-'}
									</TableCell>
									<TableCell>{viandasDiarias}</TableCell>
									<TableCell>
										{meta.entregadas}/{meta.total}
										{!meta.tieneDetalleEntregas && ' (sin detalle de entregas)'}
									</TableCell>
									<TableCell>
										<Button size="small" variant="contained" onClick={() => handleOpenEntregas(compra)}>
											Marcar
										</Button>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
				<DialogTitle>{viandaForm.id ? 'Editar plan de viandas' : 'Nuevo plan de viandas'}</DialogTitle>
				<DialogContent>
					<Box sx={{ mt: 1 }}>
						<TextField
							name="titulo"
							label="Título"
							fullWidth
							margin="normal"
							value={viandaForm.titulo}
							onChange={(e) => setViandaForm((prev) => ({ ...prev, titulo: e.target.value }))}
						/>
						<TextField
							name="descripcion"
							label="Descripción"
							fullWidth
							margin="normal"
							multiline
							rows={3}
							value={viandaForm.descripcion}
							onChange={(e) => setViandaForm((prev) => ({ ...prev, descripcion: e.target.value }))}
						/>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth margin="normal">
									<InputLabel>Días del plan</InputLabel>
									<Select
										value={viandaForm.cantidad_dias}
										label="Días del plan"
										onChange={(e) => setViandaForm((prev) => ({ ...prev, cantidad_dias: Number(e.target.value) }))}
									>
										<MenuItem value={5}>5 días</MenuItem>
										<MenuItem value={20}>20 días</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Precio por vianda"
									type="number"
									fullWidth
									margin="normal"
									value={viandaForm.precio_por_vianda}
									onChange={(e) => setViandaForm((prev) => ({ ...prev, precio_por_vianda: e.target.value }))}
								/>
							</Grid>
						</Grid>
						<FormControl fullWidth margin="normal">
							<InputLabel>Descuento asociado</InputLabel>
							<Select
								value={viandaForm.descuento_id}
								label="Descuento asociado"
								onChange={(e) => setViandaForm((prev) => ({ ...prev, descuento_id: e.target.value }))}
							>
								<MenuItem value="">Ninguno</MenuItem>
								{descuentosFiltrados.map((descuento) => (
									<MenuItem key={descuento.id} value={descuento.id}>
										{descuento.descripcion}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControlLabel
							control={
								<Checkbox
									checked={Boolean(viandaForm.activo)}
									onChange={(e) => setViandaForm((prev) => ({ ...prev, activo: e.target.checked }))}
								/>
							}
							label="Activo"
						/>
						<Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>
							Subir imagen del plan
							<input
								hidden
								type="file"
								accept="image/*"
								onChange={(e) => setViandaForm((prev) => ({ ...prev, imagen: e.target.files?.[0] || null }))}
							/>
						</Button>
						{viandaForm.imagen && (
							<Box sx={{ mt: 2 }}>
								<img
									src={typeof viandaForm.imagen === 'string' ? viandaForm.imagen : URL.createObjectURL(viandaForm.imagen)}
									alt="preview-plan-vianda"
									style={{ maxWidth: '220px', borderRadius: 8 }}
								/>
							</Box>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancelar</Button>
					<Button onClick={handleGuardarVianda} variant="contained" disabled={loading}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>
			<CustomDialog
				open={Boolean(viandaAEliminar)}
				title="Confirmar eliminación"
				content={`¿Está seguro de que desea eliminar la vianda "${viandaAEliminar?.titulo || ''}"? Esta acción no se puede deshacer.`}
				onClose={handleCerrarDialogoEliminar}
				onConfirm={handleConfirmarEliminacion}
			/>

			<Dialog open={Boolean(entregaModalCompra)} onClose={handleCloseEntregas} fullWidth maxWidth="sm">
				<DialogTitle>Marcar entregas de vianda</DialogTitle>
				<DialogContent>
					{entregaModalCompra && (
						<Box sx={{ mt: 1 }}>
							<Typography variant="body2" sx={{ mb: 2 }}>
								Seleccioná las fechas entregadas. Solo se muestran días hábiles desde la fecha de inicio del plan.
							</Typography>
							{getEntregasValidas(entregaModalCompra).length === 0 ? (
								<Alert severity="info">Esta compra no tiene fechas de entrega planificadas.</Alert>
							) : (
								Object.entries(agruparEntregasPorSemana(getEntregasValidas(entregaModalCompra))).map(
									([semana, entregasSemana]) => (
										<Box key={semana} sx={{ mb: 2 }}>
											<Typography
												variant="subtitle2"
												sx={{
													color: 'success.main',
													fontWeight: 700,
													borderBottom: '1px solid',
													borderColor: 'divider',
													mb: 1,
													pb: 0.5,
												}}
											>
												Semana {semana}
											</Typography>
											<Table size="small">
											<TableHead>
														<TableRow>
															<TableCell>Día</TableCell>
															<TableCell>Marcado el</TableCell>
															<TableCell>Nota</TableCell>
															<TableCell>Marcar</TableCell>
														</TableRow>
												</TableHead>
												<TableBody>
													{entregasSemana.map((entrega, index) => {
														const fechaEntrega = normalizarFecha(entrega.fecha_entrega);
														return (
															<TableRow key={fechaEntrega}>
																<TableCell>{`Día ${index + 1}`}</TableCell>
																<TableCell>{formatearFechaHora(entrega.entregada_at)}</TableCell>
																<TableCell>
																	<Button
																		size="small"
																		variant="outlined"
																		onClick={() => handleOpenNota(entrega)}
																	>
																		{entrega?.nota_admin ? 'Ver/editar nota' : 'Agregar nota'}
																	</Button>
																</TableCell>
																<TableCell>
																	<Checkbox
																		checked={Boolean(entregasSeleccionadas[fechaEntrega])}
																		onChange={() => handleToggleEntrega(fechaEntrega)}
																	/>
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</Box>
									),
								)
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseEntregas}>Cancelar</Button>
					<Button onClick={handleGuardarEntregas} variant="contained" disabled={loading || !entregaModalCompra}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={notaModal.open} onClose={handleCloseNota} fullWidth maxWidth="sm">
				<DialogTitle>Nota interna del día</DialogTitle>
				<DialogContent>
					<Box sx={{ mt: 1 }}>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{notaModal.fechaEntrega ? `Entrega del ${formatearFecha(notaModal.fechaEntrega)}` : 'Entrega seleccionada'}
						</Typography>
						<TextField
							fullWidth
							multiline
							minRows={4}
							label="Nota interna"
							value={notaModal.nota}
							onChange={(e) => setNotaModal((prev) => ({ ...prev, nota: e.target.value }))}
							placeholder="Escribí una nota interna para este día"
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseNota}>Cancelar</Button>
					<Button onClick={handleGuardarNota} variant="contained" disabled={loading || !entregaModalCompra}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ViandasManagement;
